import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Empty, Link, Rule, Select, ui } from '../ui';
import { formatDate } from '@goodbuild/rules/format';
import type { AdminGiftRow, DirectoryPerson } from '@api/views';
import type { AdminGame } from './admin-games';
import { ApiError, useApi, useApiMutation } from '../api';
import { Field, Notice, Screen, Table } from './shared';

type Wire<T> = T extends Date
  ? string
  : T extends (infer Item)[]
    ? Wire<Item>[]
    : T extends object
      ? { [K in keyof T]: Wire<T[K]> }
      : T;

type GiftRow = Wire<AdminGiftRow>;
type FoundPerson = Wire<DirectoryPerson>;

const GIFTS_KEY = ['admin', 'gifts'] as const;

interface GiftOutcome {
  ok: string;
}

function recipientLabel(person: Pick<FoundPerson, 'name' | 'email' | 'id'>): string {
  if (person.name && person.email) return `${person.name} — ${person.email}`;
  return person.name ?? person.email ?? person.id;
}

export function GiveAGame({
  fixedRecipient,
}: {
  fixedRecipient?: { id: string; label: string } | undefined;
}): React.ReactElement {
  const call = useApiMutation();
  const cache = useQueryClient();

  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [chosenRecipientId, setChosenRecipientId] = useState(fixedRecipient?.id ?? '');
  const [chosenProductId, setChosenProductId] = useState('');
  const [reason, setReason] = useState('');
  const [outcome, setOutcome] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const games = useApi<{ games: AdminGame[] }>(['admin', 'games'], '/admin/games');
  const found = useApi<{ people: FoundPerson[] }>(
    ['admin', 'lookup', search],
    `/admin/lookup?q=${encodeURIComponent(search)}&limit=20`,
    { enabled: search !== '' && fixedRecipient === undefined },
  );

  useEffect(() => {
    if (fixedRecipient) setChosenRecipientId(fixedRecipient.id);
  }, [fixedRecipient]);

  const people = found.data?.people ?? [];
  const recipientOptions = fixedRecipient
    ? [{ value: fixedRecipient.id, label: fixedRecipient.label }]
    : people.map((person) => ({ value: person.id, label: recipientLabel(person) }));

  const gameOptions = (games.data?.games ?? []).map((game) => ({
    value: game.id,
    label: `${game.name} — ${game.releaseState.replace(/_/g, ' ')}`,
  }));

  const ready = chosenRecipientId !== '' && chosenProductId !== '' && reason.trim() !== '';

  async function give(): Promise<void> {
    setBusy(true);
    setOutcome(null);
    try {
      const result = await call<GiftOutcome>('/admin/gifts', {
        method: 'POST',
        body: JSON.stringify({
          productId: chosenProductId,
          recipientUserId: chosenRecipientId,
          reason: reason.trim(),
        }),
      });
      setOutcome(result.ok);
      setChosenProductId('');
      setReason('');
      await cache.invalidateQueries({ queryKey: GIFTS_KEY });
    } catch (error) {
      setOutcome(error instanceof ApiError ? error.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className={ui.stack ?? ''}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !busy) void give();
      }}
    >
      {fixedRecipient ? null : (
        <>
          <Field label="Who is it for">
            <input
              className={ui.input ?? ''}
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                setSearch(searchDraft.trim());
                setChosenRecipientId('');
              }}
              placeholder="Name, email address or account id"
            />
          </Field>

          <div className={ui.actions ?? ''}>
            <button
              type="button"
              className={ui.buttonSecondary ?? ''}
              onClick={() => {
                setSearch(searchDraft.trim());
                setChosenRecipientId('');
              }}
            >
              Find them
            </button>
            <span className={ui.statLabel ?? ''}>
              {search === ''
                ? 'They need an account here before we can put a game in it.'
                : people.length === 0
                  ? `Nothing matches “${search}”.`
                  : `${people.length} match${people.length === 1 ? '' : 'es'}.`}
            </span>
          </div>
        </>
      )}

      {recipientOptions.length > 0 ? (
        <Field label={fixedRecipient ? 'Going to' : 'Pick the account'}>
          <Select
            options={recipientOptions}
            value={chosenRecipientId}
            onChange={setChosenRecipientId}
            disabled={fixedRecipient !== undefined}
            placeholder="Choose an account…"
            aria-label="Who the game is for"
          />
        </Field>
      ) : null}

      <Field label="Which game">
        <Select
          options={gameOptions}
          value={chosenProductId}
          onChange={setChosenProductId}
          placeholder="Choose a game…"
          aria-label="Which game to give"
        />
      </Field>

      <Field label="Why">
        <input
          className={ui.input ?? ''}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Press copy, a prize, putting a support problem right"
          maxLength={500}
        />
      </Field>

      <div className={ui.actions ?? ''}>
        <button type="submit" className={ui.button ?? ''} disabled={!ready || busy}>
          Give it to them
        </button>
        <span className={ui.statLabel ?? ''}>
          They get the game straight away, and an email telling them so.
        </span>
      </div>

      {outcome ? <Notice alert>{outcome}</Notice> : null}
    </form>
  );
}

export function AdminGiftsRoute(): React.ReactElement {
  const query = useApi<{ gifts: GiftRow[]; count: number }>(GIFTS_KEY, '/admin/gifts');
  const call = useApiMutation();
  const cache = useQueryClient();

  const [awaitingWithdrawal, setAwaitingWithdrawal] = useState<string | null>(null);
  const [withdrawalOutcome, setWithdrawalOutcome] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const gifts = query.data?.gifts ?? [];
  const standing = gifts.filter((gift) => gift.withdrawnAt === null).length;

  async function withdraw(giftId: string): Promise<void> {
    setBusy(true);
    setWithdrawalOutcome(null);
    try {
      const result = await call<{ ok: string }>(`/admin/gifts/${giftId}/withdraw`, {
        method: 'POST',
      });
      setWithdrawalOutcome(result.ok);
      await cache.invalidateQueries({ queryKey: GIFTS_KEY });
    } catch (error) {
      setWithdrawalOutcome(
        error instanceof ApiError ? error.message : 'That did not work. Try again.',
      );
    } finally {
      setAwaitingWithdrawal(null);
      setBusy(false);
    }
  }

  return (
    <Screen title="Gifts" aside={standing === 1 ? '1 given' : `${standing} given`} query={query}>
      <div className={ui.stack ?? ''}>
        <p className="gb-body">
          Put a game in somebody&rsquo;s library without them paying for it. They own it the same
          way a buyer does — it installs, it plays, and a review they write says the copy was free.
          Nothing here charges anybody or touches an order.
        </p>

        <GiveAGame />
      </div>

      <Rule />

      <h2 className={ui.sectionTitle ?? ''}>Everything given away</h2>

      {withdrawalOutcome ? <Notice alert>{withdrawalOutcome}</Notice> : null}

      {gifts.length === 0 ? (
        <Empty>No games given away yet. The first one shows up here.</Empty>
      ) : (
        <Table head={['Game', 'Who has it', 'Why', 'Given', 'Told', '']}>
          {gifts.map((gift) => (
            <tr key={gift.id}>
              <td>{gift.productName}</td>
              <td>
                <Link href={`/admin/people/${gift.recipientUserId}`}>
                  {gift.recipientName ?? 'No name'}
                </Link>
                <br />
                <span className={ui.statLabel ?? ''}>{gift.recipientEmail ?? 'No address'}</span>
              </td>
              <td>
                {gift.reason}
                <br />
                <span className={ui.statLabel ?? ''}>
                  by {gift.givenByName ?? gift.givenByUserId}
                </span>
              </td>
              <td className={ui.numeric ?? ''}>{formatDate(new Date(gift.givenAt))}</td>
              <td>{gift.recipientWasTold ? 'Emailed' : 'Not emailed'}</td>
              <td>
                {gift.withdrawnAt !== null ? (
                  <span className={ui.statLabel ?? ''}>
                    Taken back {formatDate(new Date(gift.withdrawnAt))}
                  </span>
                ) : awaitingWithdrawal === gift.id ? (
                  <span className={ui.actions ?? ''}>
                    <button
                      type="button"
                      className={ui.button ?? ''}
                      disabled={busy}
                      onClick={() => void withdraw(gift.id)}
                    >
                      Take it back
                    </button>
                    <button
                      type="button"
                      className={ui.buttonSecondary ?? ''}
                      disabled={busy}
                      onClick={() => setAwaitingWithdrawal(null)}
                    >
                      Leave it
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className={ui.buttonSecondary ?? ''}
                    disabled={busy}
                    onClick={() => setAwaitingWithdrawal(gift.id)}
                  >
                    Take back
                  </button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Screen>
  );
}
